import { useCallback, useEffect, useState } from "react";
import { ManufacturingDictatePad } from "@/components/manufacturing/ManufacturingDictatePad";
import { useAuth } from "@/contexts/AuthContext";
import {
  extractMaterialUsage,
  listMaterialUsageDrafts,
  type MaterialUsageDraftSummary,
  type MaterialUsageLine,
  type MaterialUsagePreview,
  type MaterialUsageReportStatus,
} from "@/lib/service/materialUsageClient";
import {
  SERVICE_MATERIALS_USAGE_REMATCH_ROLES,
  SERVICE_MATERIALS_USAGE_ROLES,
  SERVICE_MATERIALS_USAGE_WRITE_ROLES,
} from "@/lib/roles/turboServiceRoles";
import { ServiceMaterialUsageActions } from "./ServiceMaterialUsageActions";
import { ServiceMaterialUsageAfterSubmit } from "./ServiceMaterialUsageAfterSubmit";
import { ServiceMaterialUsageReview } from "./ServiceMaterialUsageReview";
import { useMaterialUsageCommands } from "./useMaterialUsageCommands";
import { ghostBtnClass, primaryBtnClass } from "./serviceShared";

export function ServiceMaterialsUsagePanel() {
  const auth = useAuth();
  const canWrite = auth.hasRole(...SERVICE_MATERIALS_USAGE_WRITE_ROLES);
  const canRematch = auth.hasRole(...SERVICE_MATERIALS_USAGE_REMATCH_ROLES);
  const canCorrect = auth.hasRole(...SERVICE_MATERIALS_USAGE_ROLES);
  const [preview, setPreview] = useState<MaterialUsagePreview | null>(null);
  const [serialText, setSerialText] = useState("");
  const [transcript, setTranscript] = useState<string | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [reportStatus, setReportStatus] = useState<MaterialUsageReportStatus | null>(null);
  const [correctsReportId, setCorrectsReportId] = useState<string | null>(null);
  const [mailStatus, setMailStatus] = useState<string | null>(null);
  const [hasFile, setHasFile] = useState(false);
  const [drafts, setDrafts] = useState<MaterialUsageDraftSummary[]>([]);
  const accountName =
    auth.profile?.full_name?.trim() ||
    auth.profile?.display_name?.trim() ||
    auth.profile?.email?.trim() ||
    "your account";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reloadDrafts = useCallback(async () => {
    const result = await listMaterialUsageDrafts();
    if (result.ok) setDrafts(result.data.reports);
  }, []);

  useEffect(() => {
    void reloadDrafts();
  }, [reloadDrafts]);

  function applyPreview(next: MaterialUsagePreview) {
    setPreview(next);
    setSerialText(next.serial_text);
  }

  async function onExtract(text: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    const result = await extractMaterialUsage({ transcript: text });
    setBusy(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setTranscript(text);
    setDraftId(null);
    setReportStatus(null);
    setCorrectsReportId(null);
    setMailStatus(null);
    setHasFile(false);
    applyPreview(result.data.preview);
  }

  async function onPlant(plantId: string) {
    if (!preview) return;
    setBusy(true);
    const result = await extractMaterialUsage({
      plant_id: plantId,
      serial_text: serialText,
      lines: preview.lines.map((line) => ({ quantity: line.quantity, spoken_phrase: line.spoken_phrase })),
    });
    setBusy(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    applyPreview({ ...result.data.preview, serial_text: serialText });
    setSerialText(serialText);
  }

  function onLine(index: number, patch: Partial<MaterialUsageLine>) {
    setPreview((current) => {
      if (!current) return current;
      const lines = current.lines.map((line, lineIndex) => (lineIndex === index ? { ...line, ...patch } : line));
      return { ...current, lines };
    });
  }

  const { openDraft, onSave, onCertify, onReopen, onSubmit, onCorrect } = useMaterialUsageCommands({
    preview,
    draftId,
    correctsReportId,
    serialText,
    transcript,
    canWrite,
    reloadDrafts,
    setPreview,
    setSerialText,
    setTranscript,
    setDraftId,
    setReportStatus,
    setCorrectsReportId,
    setMailStatus,
    setHasFile,
    setBusy,
    setError,
    setNotice,
  });

  return (
    <div className="w-full text-left space-y-4">
      <div className="space-y-1">
        <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Materials Usage</h2>
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          One serial, one plant, and the materials taken. Certify stamps your name. Submit draws stock and sends the report.
        </p>
      </div>
      {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      {notice ? <p className="text-sm text-emerald-700 dark:text-emerald-300">{notice}</p> : null}
      {canWrite ? (
        <ManufacturingDictatePad
          placeholder='Serial 98369, taking 2 77" black metal roof panels from the Orangeburg plant'
          extractLabel={busy ? "Matching…" : "Match materials"}
          disabled={busy}
          onComplete={(text) => void onExtract(text)}
        />
      ) : (
        <p className="text-sm text-gray-600 dark:text-zinc-400">You can read reports. A repair technician enters them.</p>
      )}
      {preview ? (
        <>
          <ServiceMaterialUsageReview
            preview={preview}
            serialText={serialText}
            onSerialText={setSerialText}
            onPlant={(plantId) => void onPlant(plantId)}
            onLine={onLine}
            onRemoveLine={(index) =>
              setPreview((current) =>
                current ? { ...current, lines: current.lines.filter((_, lineIndex) => lineIndex !== index) } : current,
              )
            }
            readOnly={!canWrite || reportStatus === "certified" || reportStatus === "submitted" || Boolean(correctsReportId)}
            quantityOnly={Boolean(correctsReportId) && reportStatus === "draft"}
          />
          {(canWrite || correctsReportId) && (reportStatus === null || reportStatus === "draft") ? (
            <button type="button" className={primaryBtnClass} disabled={busy} onClick={() => void onSave()}>
              {correctsReportId ? "Save correction" : "Save draft"}
            </button>
          ) : null}
          {reportStatus === "submitted" && draftId ? (
            <ServiceMaterialUsageAfterSubmit
              reportId={draftId}
              preview={preview}
              canRematch={canRematch}
              canCorrect={canCorrect}
              canResend={canWrite || canRematch}
              hasFile={hasFile}
              mailStatus={mailStatus}
              busy={busy}
              onMailStatus={setMailStatus}
              onCorrect={() => void onCorrect()}
              onError={setError}
              onRematched={(index, materialId) => {
                const material = preview.catalog.find((row) => row.id === materialId);
                onLine(index, {
                  material_id: materialId,
                  material_name: material?.name ?? null,
                  unit: material?.unit ?? null,
                  match_status: materialId ? "rematched" : "unmatched",
                });
              }}
            />
          ) : canWrite || correctsReportId ? (
            <ServiceMaterialUsageActions
              status={reportStatus}
              accountName={accountName}
              busy={busy}
              mailStatus={mailStatus}
              onCertify={() => void onCertify()}
              onReopen={() => void onReopen()}
              onSubmit={() => void onSubmit()}
            />
          ) : null}
        </>
      ) : null}
      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Reports</h3>
        {drafts.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-zinc-400">No materials usage reports yet.</p>
        ) : (
          <ul className="space-y-1">
            {drafts.map((draft) => (
              <li key={draft.id}>
                <button type="button" className={ghostBtnClass} onClick={() => void openDraft(draft.id)}>
                  {draft.serial_text || "No serial"} · {draft.plant_name || "No plant"} · {draft.status} · {draft.line_count} lines
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
